import React, { useState, useEffect } from "react";
import {
  Modal,
  Form,
  Space,
  Checkbox,
  Alert,
  notification,
  Descriptions,
  theme,
  Switch,
} from "antd";
import { NumberOutlined, DeploymentUnitOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import QuantityInputMethod from "./QuantityInputMethod";
import { QuantityInputMethod as QuantityInputMethodEnum } from "../../../models/order-product/types";
import { IProduct } from "../../../models/product/response";
import OrderProductService from "../../../services/order-product";
import { httpclient } from "../../../services/http-client";
import { IStore } from "../../../models/store/response";
import StoreSelector from "./StoreSelector";
import {
  accountingToByUnit,
  countAndPartitionToByUnit,
  formatQuantityByUnit,
  getQuantityPrecision,
  getQuantityStepByUnit,
  quantityInputToByUnit,
} from "../../../utils/helpers/quantity";

interface OrderProduct {
  id?: number;
  product: IProduct;
  count?: number;
  unitPartition?: {
    id: number;
    name: string;
    percentage: string | number;
  };
  store?: IStore;
  store_id?: number;
  availableWeight?: number;
  quantityByUnit?: number;
  requestedQuantityByUnit?: number;
  originalRequestedQuantityByUnit?: number;
  baseQuantityByUnit?: number;
  reservedQuantityByUnit?: number;
  quantityAccounting?: number;
  reservedQuantityAccounting?: number;
  factorAccountingSystem?: number;
  decimalPlaces?: number;
  quantityStepByUnit?: number;
  isAccepted?: boolean;
}

interface Props {
  visible: boolean;
  orderId: number;
  orderProduct: OrderProduct | null;
  onCancel: () => void;
  onSuccess: () => void;
}

const EditProductModal: React.FC<Props> = ({
  visible,
  orderId,
  orderProduct,
  onCancel,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [markAsAccepted, setMarkAsAccepted] = useState(true);
  const [storeId, setStoreId] = useState<number | undefined>(undefined);
  const [quantityData, setQuantityData] = useState<{
    method: QuantityInputMethodEnum;
    count?: number;
    unitPartitionId?: number;
    availableWeight?: number;
  }>({
    method: QuantityInputMethodEnum.BY_COUNT,
    count: 1,
  });
  const [orderProductService] = useState(
    () => new OrderProductService(httpclient),
  );

  useEffect(() => {
    if (visible && orderProduct) {
      initializeForm();
    }
  }, [visible, orderProduct]);

  const getQuantityMeta = () => {
    const storeQty = orderProduct?.product?.storeQuantities?.find(
      (sq) => sq.store_id === (orderProduct.store_id || orderProduct.store?.id),
    );
    const factor =
      orderProduct?.factorAccountingSystem ||
      orderProduct?.product?.unit?.factorAccountingSystem ||
      orderProduct?.product?.unit?.factor_accounting_system ||
      storeQty?.factorAccountingSystem ||
      storeQty?.factor_accounting_system ||
      1;
    const decimalPlaces =
      orderProduct?.decimalPlaces ??
      storeQty?.decimalPlaces ??
      orderProduct?.product?.unit?.decimalPlaces;

    return {
      factor,
      decimalPlaces,
      quantityStepByUnit:
        orderProduct?.quantityStepByUnit ??
        storeQty?.quantityStepByUnit ??
        orderProduct?.product?.unit?.quantityStepByUnit ??
        getQuantityStepByUnit(undefined, decimalPlaces, factor),
    };
  };

  const getCurrentReservedByUnit = () => {
    if (!orderProduct) return undefined;

    return (
      orderProduct.reservedQuantityByUnit ??
      orderProduct.quantityByUnit ??
      orderProduct.availableWeight ??
      accountingToByUnit(
        orderProduct.reservedQuantityAccounting ??
          orderProduct.quantityAccounting,
        getQuantityMeta().factor,
      ) ??
      countAndPartitionToByUnit(orderProduct.count, orderProduct.unitPartition)
    );
  };

  const getRequestedQuantityByUnit = () => {
    if (!orderProduct) return undefined;

    return (
      orderProduct.requestedQuantityByUnit ??
      orderProduct.originalRequestedQuantityByUnit ??
      orderProduct.baseQuantityByUnit ??
      orderProduct.quantityByUnit ??
      countAndPartitionToByUnit(orderProduct.count, orderProduct.unitPartition)
    );
  };

  const initializeForm = () => {
    if (!orderProduct) return;

    const productStoreId = orderProduct.store_id || orderProduct.store?.id;
    const quantityMeta = getQuantityMeta();
    const currentReservedByUnit = getCurrentReservedByUnit();

    setStoreId(productStoreId);
    setQuantityData({
      method: QuantityInputMethodEnum.BY_WEIGHT,
      availableWeight:
        currentReservedByUnit ?? quantityMeta.quantityStepByUnit,
    });
    setMarkAsAccepted(orderProduct.isAccepted !== false);
  };

  const resetForm = () => {
    form.resetFields();
    setMarkAsAccepted(true);
    setStoreId(undefined);
    setQuantityData({
      method: QuantityInputMethodEnum.BY_COUNT,
    });
  };

  const getStoreQuantityInfo = () => {
    const currentStoreId = orderProduct?.store_id || orderProduct?.store?.id;
    if (!orderProduct?.product?.storeQuantities || !currentStoreId) {
      return null;
    }

    const storeQty = orderProduct.product.storeQuantities.find(
      (sq) => sq.store_id === currentStoreId,
    );

    if (!storeQty) {
      return null;
    }

    const factor =
      orderProduct.product.unit?.factorAccountingSystem ||
      orderProduct.product.unit?.factor_accounting_system ||
      storeQty.factorAccountingSystem ||
      storeQty.factor_accounting_system;
    const availableUnits =
      storeQty.availableQuantityByUnit ??
      (factor ? accountingToByUnit(storeQty.quantity, factor) : undefined) ??
      0;

    return {
      available: storeQty.available,
      quantity: availableUnits,
      storeName: storeQty.store_name,
      factor: factor ?? 1,
      decimalPlaces: storeQty.decimalPlaces ?? orderProduct.product.unit?.decimalPlaces,
    };
  };

  const validateForm = (): boolean => {
    const hasExistingStore = !!(
      orderProduct?.store_id || orderProduct?.store?.id
    );
    if (!hasExistingStore && !storeId) {
      notification.error({
        message: t("validation_error"),
        description: t("please_select_store"),
      });
      return false;
    }

    const { method, count, availableWeight } = quantityData;

    if (method === QuantityInputMethodEnum.BY_COUNT) {
      if (!count || count < 1) {
        notification.error({
          message: t("validation_error"),
          description: t("count_must_be_at_least_1"),
        });
        return false;
      }
    }

    if (method === QuantityInputMethodEnum.BY_WEIGHT) {
      if (
        availableWeight === undefined ||
        availableWeight === null ||
        availableWeight <= 0
      ) {
        notification.error({
          message: t("validation_error"),
          description: t("weight_must_be_greater_than_zero"),
        });
        return false;
      }
    }

    return true;
  };

  const handleMethodChange = (checked: boolean) => {
    const newMethod = checked
      ? QuantityInputMethodEnum.BY_WEIGHT
      : QuantityInputMethodEnum.BY_COUNT;
    const quantityMeta = getQuantityMeta();
    setQuantityData({
      method: newMethod,
      count:
        newMethod === QuantityInputMethodEnum.BY_COUNT
          ? quantityData.count
          : undefined,
      unitPartitionId:
        newMethod === QuantityInputMethodEnum.BY_COUNT
          ? quantityData.unitPartitionId
          : undefined,
      availableWeight:
        newMethod === QuantityInputMethodEnum.BY_WEIGHT
          ? quantityData.availableWeight ?? quantityMeta.quantityStepByUnit
          : undefined,
    });
  };

  const handleSubmit = async () => {
    if (!validateForm() || !orderProduct?.id) return;

    setLoading(true);

    try {
      const targetReservedQuantityByUnit = quantityInputToByUnit(
        quantityData,
        orderProduct.product,
      );
      const updateData: any = {
        productOrderId: orderProduct.id,
        targetReservedQuantityByUnit,
        isAccepted: markAsAccepted,
      };

      if (quantityData.unitPartitionId !== undefined) {
        updateData.unitPartitionId = quantityData.unitPartitionId;
      }

      if (!(orderProduct?.store_id || orderProduct?.store?.id) && storeId) {
        updateData.storeId = storeId;
      }

      await orderProductService.updateProduct(updateData);

      notification.success({
        message: t("success"),
        description: t("product_updated_successfully"),
      });

      onSuccess();
      resetForm();
    } catch (error: any) {
      console.error("Error updating product:", error);

      if (!error?.response && !error?.errorType) {
        notification.error({
          message: t("error"),
          description: error?.message || t("failed_to_update_product"),
        });
      }
    } finally {
      setLoading(false);
    }
  };

  if (!orderProduct) return null;
  const quantityMeta = getQuantityMeta();
  const requestedQuantityByUnit = getRequestedQuantityByUnit();
  const currentReservedByUnit = getCurrentReservedByUnit();

  return (
    <Modal
      title={t("edit_product")}
      open={visible}
      onCancel={onCancel}
      width="90%"
      style={{ maxWidth: 700 }}
      onOk={handleSubmit}
      okText={t("save_changes")}
      cancelText={t("cancel")}
      confirmLoading={loading}
      aria-labelledby="edit-product-modal-title"
      aria-describedby="edit-product-modal-description"
      okButtonProps={{ "aria-label": t("save_changes") } as any}
      cancelButtonProps={{ "aria-label": t("cancel") } as any}
    >
      <Form form={form} layout="vertical">
        <div
          style={{
            padding: "20px",
            backgroundColor: token.colorBgContainer,
            borderRadius: token.borderRadiusLG,
            border: `1px solid ${token.colorBorder}`,
            marginBottom: 16,
          }}
        >
          <Space direction="vertical" size="middle" style={{ width: "100%" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "8px 12px",
                backgroundColor: token.colorFillQuaternary,
                borderRadius: token.borderRadius,
              }}
            >
              <span
                style={{ fontWeight: 500, color: token.colorTextSecondary }}
              >
                {t("product_name")}
              </span>
              <span style={{ fontWeight: 600, color: token.colorText }}>
                {orderProduct.product.name}
              </span>
            </div>
            {orderProduct.product.unit && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "8px 12px",
                  backgroundColor: token.colorFillQuaternary,
                  borderRadius: token.borderRadius,
                }}
              >
                <span
                  style={{ fontWeight: 500, color: token.colorTextSecondary }}
                >
                  {t("unit")}
                </span>
                <span style={{ fontWeight: 600, color: token.colorText }}>
                  {orderProduct.product.unit.name}
                </span>
              </div>
            )}
          </Space>
        </div>

        {/* Store Selector */}
        <div
          style={{
            padding: "20px",
            backgroundColor: token.colorBgContainer,
            borderRadius: token.borderRadiusLG,
            border: `1px solid ${token.colorBorder}`,
            marginBottom: 24,
          }}
        >
          <label
            style={{
              fontWeight: 600,
              fontSize: 14,
              color: token.colorText,
              display: "block",
              marginBottom: 8,
            }}
          >
            {t("store")} <span style={{ color: token.colorError }}>*</span>
          </label>
          <StoreSelector
            value={storeId}
            onChange={setStoreId}
            placeholder={t("select_store_to_view_quantities")}
            disabled={!!(orderProduct?.store_id || orderProduct?.store?.id)}
          />
          {(orderProduct?.store_id || orderProduct?.store?.id) && (
            <div
              style={{
                marginTop: 8,
                padding: "8px 12px",
                backgroundColor: token.colorInfoBg,
                borderRadius: token.borderRadius,
                border: `1px solid ${token.colorInfoBorder}`,
                color: token.colorInfoText,
                fontSize: 13,
              }}
            >
              {t("store_already_selected_cannot_change")}
            </div>
          )}
        </div>

        <Space direction="vertical" style={{ width: "100%" }} size="middle">
          <div
            style={{
              padding: "20px",
              backgroundColor: token.colorBgContainer,
              borderRadius: token.borderRadiusLG,
              border: `1px solid ${token.colorBorder}`,
            }}
          >
            <h4
              style={{
                marginTop: 0,
                marginBottom: 16,
                color: token.colorText,
                fontSize: 16,
                fontWeight: 600,
                paddingBottom: 12,
                borderBottom: `2px solid ${token.colorPrimary}`,
              }}
            >
              {t("current_values")}
            </h4>
            <Space direction="vertical" size="middle" style={{ width: "100%" }}>
              {(() => {
                const storeQtyInfo = getStoreQuantityInfo();
                if (storeQtyInfo) {
                  return (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "8px 12px",
                        backgroundColor: storeQtyInfo.available
                          ? token.colorSuccessBg
                          : token.colorErrorBg,
                        borderRadius: token.borderRadius,
                        border: `1px solid ${storeQtyInfo.available ? token.colorSuccessBorder : token.colorErrorBorder}`,
                      }}
                    >
                      <span
                        style={{
                          fontWeight: 500,
                          color: token.colorTextSecondary,
                        }}
                      >
                        {t("store_available_quantity")}
                      </span>
                      <span
                        style={{
                          fontSize: 15,
                          fontWeight: 600,
                          color: storeQtyInfo.available
                            ? token.colorSuccess
                            : token.colorError,
                        }}
                      >
                        {storeQtyInfo.available
                          ? `${formatQuantityByUnit(
                              storeQtyInfo.quantity,
                              storeQtyInfo.decimalPlaces,
                              storeQtyInfo.factor,
                            )} ${orderProduct.product.unit?.name || ""}`
                          : t("unavailable")}
                      </span>
                    </div>
                  );
                }
                return null;
              })()}
              {[
                {
                  key: "requested",
                  label: t("requested_quantity_by_unit"),
                  value: requestedQuantityByUnit,
                },
                {
                  key: "reserved",
                  label: t("current_reserved_quantity_by_unit"),
                  value: currentReservedByUnit,
                },
              ].map((item) => (
                <div
                  key={item.key}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "8px 12px",
                    backgroundColor: token.colorFillQuaternary,
                    borderRadius: token.borderRadius,
                  }}
                >
                  <span
                    style={{ fontWeight: 500, color: token.colorTextSecondary }}
                  >
                    {item.label}
                  </span>
                  <span style={{ fontSize: 15, fontWeight: 600 }}>
                    {formatQuantityByUnit(
                      item.value,
                      quantityMeta.decimalPlaces,
                      quantityMeta.factor,
                    )}{" "}
                    {orderProduct.product.unit?.name || ""}
                  </span>
                </div>
              ))}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "8px 12px",
                  backgroundColor: token.colorFillQuaternary,
                  borderRadius: token.borderRadius,
                }}
              >
                <span
                  style={{ fontWeight: 500, color: token.colorTextSecondary }}
                >
                  {t("status")}
                </span>
                {orderProduct.isAccepted === false ? (
                  <span
                    style={{
                      color: token.colorError,
                      fontWeight: 600,
                      fontSize: 14,
                    }}
                  >
                    {t("unaccepted")}
                  </span>
                ) : (
                  <span
                    style={{
                      color: token.colorSuccess,
                      fontWeight: 600,
                      fontSize: 14,
                    }}
                  >
                    {t("accepted")}
                  </span>
                )}
              </div>
            </Space>
          </div>

          <div
            style={{
              padding: "20px",
              backgroundColor: token.colorBgContainer,
              borderRadius: token.borderRadiusLG,
              border: `2px solid ${token.colorPrimary}`,
            }}
          >
            {(() => {
              const canUseDirectUnitInput =
                !!orderProduct.product.unitPartition?.length ||
                orderProduct.product.soldByUnit ||
                getQuantityPrecision(
                  quantityMeta.decimalPlaces,
                  quantityMeta.factor,
                ) > 0;

              return (
                <>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      marginBottom: 16,
                      paddingBottom: 12,
                      borderBottom: `2px solid ${token.colorPrimary}`,
                    }}
                  >
                    <h4
                      style={{
                        margin: 0,
                        color: token.colorPrimary,
                        fontSize: 16,
                        fontWeight: 600,
                      }}
                    >
                      {t("new_values")}
                    </h4>
                    {canUseDirectUnitInput && (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-end",
                          gap: 6,
                        }}
                      >
                        <Switch
                          checked={
                            quantityData.method ===
                            QuantityInputMethodEnum.BY_WEIGHT
                          }
                          onChange={handleMethodChange}
                          checkedChildren={<DeploymentUnitOutlined />}
                          unCheckedChildren={<NumberOutlined />}
                        />
                        <span
                          style={{
                            fontSize: 11,
                            color: token.colorTextSecondary,
                            textAlign: "right",
                            maxWidth: 200,
                            lineHeight: 1.4,
                          }}
                        >
                          {t("quantity_method_helper")}
                        </span>
                      </div>
                    )}
                  </div>

                  <Form.Item style={{ marginBottom: 20 }}>
                    <QuantityInputMethod
                      value={quantityData}
                      onChange={setQuantityData}
                      product={orderProduct.product}
                      decimalPlaces={quantityMeta.decimalPlaces}
                      factorAccountingSystem={quantityMeta.factor}
                      quantityStepByUnit={quantityMeta.quantityStepByUnit}
                    />
                  </Form.Item>

                  <Form.Item style={{ marginBottom: 0 }}>
                    <Checkbox
                      checked={markAsAccepted}
                      onChange={(e) => setMarkAsAccepted(e.target.checked)}
                      style={{ fontSize: 14 }}
                    >
                      <span style={{ fontWeight: 500 }}>
                        {t("mark_as_accepted")}
                      </span>
                    </Checkbox>
                  </Form.Item>
                </>
              );
            })()}
          </div>
        </Space>
      </Form>
    </Modal>
  );
};

export default EditProductModal;
